/**
 * What the server should do next, without being asked. The server (and the simulation) calls
 * `dueActions` whenever the state changes or the clock reaches an alarm, appends what comes back,
 * and calls again until nothing is due. Each call returns at most one step, computed from the
 * state as it is now, so the loop can never run ahead of the log.
 *
 * The policy (5 Oct 2026):
 * - a round finishes when its clock runs out OR every court has entered a score;
 * - the changeover ends on its clock and, with autoAdvance on, rolls straight into the next round
 *   whether or not every score is in (an owed score is flagged, never waited for);
 * - unless NOT ONE court of the round just gone has a score or a void: then the hall has walked
 *   off, and the next round waits for one score or for an admin's Start (13 Oct 2026);
 * - a score or void that lets that hold go does not spring a round on the hall: the next one
 *   starts one changeover after it went in, so the wall can count down (an admin's Start is at once);
 * - since decision 41 (14 Oct 2026) autoAdvance is off for a new tournament: the clock still
 *   finishes the game and runs the changeover, and then the next round waits for an admin's Start
 *   (the admin screen, or the watch); the two rules above only matter with the switch on;
 * - a preview of the next round is kept drawn whenever there is none, and drawn again when it
 *   leaves out someone who is here (an admin added them, or a round taken back brought back an
 *   older line-up; 16 Oct 2026) (during a wind down, the
 *   plan's next round; once its last round is played nothing more is drawn, so nothing starts);
 * - with random events on, a starting round has randomEventChance of carrying a mini game for the
 *   whole hall, never two rounds running: a scoring one for the whole game, or a handicap as a
 *   timed twist one to three minutes in, while every court is still playing.
 *   With mini games switched off (the Mini games switch on the Events tab) nothing is rolled.
 * Nothing happens while everything is paused.
 */

import { makeRng, roundSeed } from "./rng";
import { draftRound } from "./scheduler";
import { allScoresIn, availablePlayers, currentRound, heldForScores, presentIn, startsByItselfAt } from "./selectors";
import type { Lineup, TournamentEvent, TournamentState } from "./types";

const EVENT_SALT = 424242;

/**
 * When the server should next wake up by itself, or null when nothing is timed. Between rounds
 * that is the start a late score has set going, if there is one. Give `now` to be told only of a
 * time still to come: a server that sets a real alarm must not be woken again and again for a
 * start that has passed and cannot happen (too few players, the last whistle).
 */
export function nextAlarm(state: TournamentState, now?: number): number | null {
  if (state.pausedAll) return null;
  if (state.clock.phase === "idle") {
    const at = startsByItselfAt(state);
    if (at === null || !state.config.autoAdvance || pastLastWhistle(state, at)) return null;
    return now !== undefined && now >= at ? null : at;
  }
  return state.clock.phaseEndsAt;
}

/**
 * When the server should try again after one of its own steps was refused: after the wait, or at
 * the phase's end when that is later. Between rounds too, where nothing else is timed. Never
 * while everything is paused.
 */
export function retryAlarm(state: TournamentState, now: number, waitMs: number): number | null {
  if (state.pausedAll) return null;
  return Math.max(nextAlarm(state) ?? 0, now + waitMs);
}

/**
 * A twist the autopilot places starts between one and three minutes in. The game length is only
 * a cap: real games last six to eight minutes, so a later one would land on empty courts.
 */
export const TWIST_EARLIEST_MS = 60_000;
export const TWIST_LATEST_MS = 180_000;

export interface Started {
  lineup: Lineup;
  eventIds: string[];
  twist?: { eventId: string; afterMs: number };
}

/** A starting line-up, with a mini game rolled onto it if random events are on. */
export function withRandomEvent(state: TournamentState, lineup: Lineup): Started {
  const none: Started = { lineup, eventIds: [] };
  // mini games switched off by an admin: none, whatever the Random switch says
  if (state.miniGamesOff || !state.config.randomEvents || !state.config.eventTypes.length) return none;
  // never two rounds running
  const last = currentRound(state);
  if (last && (last.twist || last.eventIds.length || last.games.some((g) => g.eventId))) return none;
  const rng = makeRng(roundSeed(state.seed, state.rounds.length + 1, EVENT_SALT));
  if (rng.next() >= state.config.randomEventChance) return none;
  const type = rng.pick(state.config.eventTypes);
  if (!type) return none;
  if (type.kind === "scoring") return { lineup, eventIds: [type.id] };
  // a game too short to hold a twist (a test's four second round) goes without
  if (state.config.twistMs > state.config.gameMs) return none;
  const latest = Math.min(TWIST_LATEST_MS, state.config.gameMs - state.config.twistMs);
  const afterMs = latest > TWIST_EARLIEST_MS
    ? TWIST_EARLIEST_MS + Math.floor(rng.next() * (latest - TWIST_EARLIEST_MS))
    : Math.max(0, Math.floor((state.config.gameMs - state.config.twistMs) / 2));
  return { lineup, eventIds: [], twist: { eventId: type.id, afterMs } };
}

/** True when a round started now could not finish by the last whistle. */
export function pastLastWhistle(state: TournamentState, now: number): boolean {
  const whistle = state.config.lastWhistleAt;
  return whistle !== null && now + state.config.gameMs > whistle;
}

function freshDraft(state: TournamentState): TournamentEvent | null {
  if (availablePlayers(state).length < state.config.playersPerCourt) return null;
  const lineup = draftRound(state);
  if (!lineup.courts.length) return null;
  return { type: "round.drafted", lineup };
}

/**
 * True when the preview leaves out someone who is here: an admin added them as here, or a round
 * taken back brought back a line-up drawn before they tapped I'm here. A fresh draw lists everyone
 * here on a court or sitting out, so drawing again settles it and never loops.
 */
function draftMissesSomeone(state: TournamentState): boolean {
  if (!state.draft) return false;
  const listed = new Set(presentIn(state.draft));
  return availablePlayers(state).some((id) => !listed.has(id));
}

export function dueActions(state: TournamentState, now: number): TournamentEvent[] {
  if (state.pausedAll) return [];
  const round = currentRound(state);
  const ends = state.clock.phaseEndsAt;

  switch (state.clock.phase) {
    case "playing": {
      if (round && state.clock.roundNo === round.no && (allScoresIn(round) || (ends !== null && now >= ends))) {
        return [{ type: "round.finished" }];
      }
      // In Mexicano the next line-up follows the board, so it is not drawn until every score is
      // in (Abhi, 5 Oct 2026: no redrawing on the projector as each court's score lands).
      if (state.mode !== "mexicano" && (!state.draft || draftMissesSomeone(state))) {
        const draft = freshDraft(state);
        return draft ? [draft] : [];
      }
      return [];
    }
    case "changeover": {
      if (ends !== null && now >= ends) return [{ type: "round.closed" }];
      if (!state.draft || draftMissesSomeone(state)) {
        const draft = freshDraft(state);
        return draft ? [draft] : [];
      }
      return [];
    }
    case "idle": {
      if (!state.draft) {
        const draft = freshDraft(state);
        return draft ? [draft] : [];
      }
      // someone here is not on the preview: draw it again (when nothing can be drawn, carry on)
      if (draftMissesSomeone(state)) {
        const draft = freshDraft(state);
        if (draft) return [draft];
      }
      // nobody scored the round just gone: the hall has walked off, so wait for a score or a Start
      if (heldForScores(state)) return [];
      // a late score let it go: the next round waits one changeover from then, so the hall is warned
      const from = startsByItselfAt(state);
      if (from !== null && now < from) return [];
      if (state.config.autoAdvance && state.rounds.length > 0 && !pastLastWhistle(state, now)) {
        return [{ type: "round.started", ...withRandomEvent(state, state.draft) }];
      }
      return [];
    }
    default:
      return [];
  }
}
